import React, { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import CategoryPageConstructor from 'src/constructors/pageConstructors/CategoryPageConstructor';
import ContentPageConstructor from 'src/constructors/pageConstructors/ContentPageConstructor';
import HomePageConstructor from 'src/constructors/pageConstructors/HomePageConstructor';
import StaticPageConstructor from 'src/constructors/pageConstructors/StaticPagesConstructor';
import { envStrToBoolean, isTokenExpired } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { InfoAndRulesPage } from 'src/pages/InfoAndRulesPage/InfoAndRulesPage';
import { Signup } from 'src/pages/Signup/Signup';
import StaticHomePage from 'src/pages/StaticHomePage';
import { refreshTokenRequest } from 'src/services/authorizationApi';
import { setSelectedCategory } from 'src/store/app/actions';
import { setSelectedProvider, setSelectedTag } from 'src/store/games/actions';
import { setLogout } from 'src/store/user/actions';
import { NavigationItem } from 'src/types/res-dto/categories-res';
import { RootState } from 'src/types/store-types';
import { ONE_HOUR, PAPIGAMES_AFFILATES } from 'src/utils/constants';
import { lazyWithCustomError } from 'src/utils/lazyWithCustomError';
import BetConstructSportsbook from '../base-components/BetConstruct_Sportsbook';
import { BuilderIoPreview } from '../base-components/BuilderIo';
import CasinoTournament from '../base-components/CasinoTournament';
import Cms_Wager from '../base-components/CMS_Wager';
import FirstSportbook from '../base-components/FirstSportbook';
import FunMission from '../base-components/FunMission';
import Iframe from '../base-components/Iframe';
import Race from '../base-components/Race';
import RaffleDraw from '../base-components/RaffleDraw';
import SafePromo from '../base-components/SafePromo';
import MainLayout from '../layouts/MainLayout';

const NotFoundPage = lazyWithCustomError(() => import('src/pages/NotFoundPage'));
type Route = {
  paths: string[];
  element: JSX.Element;
};
const Routing: FC = () => {
  const _logout = useConnectedAction(setLogout);
  const _setSelectedTag = useConnectedAction(setSelectedTag);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const _setSelectedProvider = useConnectedAction(setSelectedProvider);
  const { user } = useSelector((state: RootState) => state.user);
  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { pageConfigs, navigation } = useSelector((state: RootState) => state.configs);

  const [defaultPage, setDefaultPage] = useState<JSX.Element | null>(null);

  const setNewStorageValues = (newUser: any, exp: number): void => {
    import('dayjs').then((dayjs) => {
      const now = dayjs.default();
      const storedDate = now.add(exp, 'seconds').valueOf();

      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('storedDate', storedDate.toString());
    });
  };
  if (
    user &&
    localStorage.getItem('storedDate') &&
    Number(localStorage.getItem('storedDate')) - new Date().valueOf() < ONE_HOUR
  ) {
    refreshTokenRequest()
      .then((res) => {
        const newUser = { ...user, token: res.result.token };
        setNewStorageValues(newUser, res.result.exp);
      })
      .catch((error) => {
        console.error('Failed to refresh token', error);
        // Handle token refresh failure (e.g., logout the user, show an error message)
      });
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const renderPage = (route: NavigationItem): any => {
    let component;
    let sections;
    let mainPath: string;
    const aliasPaths: string[] = [];
    const childrenPaths: string[] = [];

    switch (route?.type) {
      case 'home':
        sections = pageConfigs.filter((f: { type: string }) => f.type === 'home')[0]?.sections;
        component =
          process.env.REACT_APP_HOME_PAGE_STATIC === 'true' ? (
            <StaticHomePage />
          ) : (
            <HomePageConstructor settings={sections} type={route.type} />
          );
        mainPath = '/home';
        break;
      case 'signup':
        mainPath = '/signup';
        component = <Signup />;
        break;
      case 'infoAndRulesPage':
        mainPath = `/${route.slug || route.name}`;
        route.slugAliases?.forEach((slugAliase) => aliasPaths.push(slugAliase));
        component = <InfoAndRulesPage slug={route.slug} name={route.name} />;
        break;
      case 'static':
        childrenPaths.push(':staticPathParam');
        childrenPaths.push(':tab');
        component = <StaticPageConstructor page={route.name} />;
        mainPath = route.name;
        break;
      case 'fun-mission':
        component = <FunMission />;
        mainPath = `/category/${route.name}`;
        break;
      case 'raffleDraw':
        component = <RaffleDraw />;
        mainPath = `/category/${route.slug || route.name}`;
        route.slugAliases?.forEach((slugAliase) => aliasPaths.push(`/category/${slugAliase}`));
        break;
      case 'builder-io-preview':
        childrenPaths.push(':tab');
        component = <BuilderIoPreview />;
        mainPath = '/builder-io-preview/:name';
        break;
      case 'casinoTournament':
        component = <CasinoTournament />;
        mainPath = `/category/${route.slug || route.name}`;
        route.slugAliases?.forEach((slugAliase) => aliasPaths.push(`/category/${slugAliase}`));
        break;
      case 'race':
      case 'fsRace':
        component = <Race />;
        mainPath = `/category/${route.slug || route.name}`;
        route.slugAliases?.forEach((slugAliase) => aliasPaths.push(`/category/${slugAliase}`));
        break;
      case 'safe-promo':
        component = <SafePromo />;
        mainPath = `/category/${route.name}`;
        break;
      case 'staticIframe':
      case 'tombala':
      case 'pokerIframe':
      case 'cybersport':
        component = <Iframe type={route?.type} />;
        mainPath = `/category/${route.name}`;
        break;
      case 'liveSport':
      case 'sport':
        const sportType = route.type === 'liveSport' ? 'live' : 'sport';

        component = <Iframe type={route?.type} />;

        if (envStrToBoolean(process.env.REACT_APP_CMS_WAGER)) {
          component = <Cms_Wager sportType={sportType} />;
        }

        if (envStrToBoolean(process.env.REACT_APP_FIRST_SPORTBOOK)) {
          component = <FirstSportbook />;
        }

        if (envStrToBoolean(process.env.REACT_APP_BC_SPORT_BOOK)) {
          component = <BetConstructSportsbook sportType={sportType} />;
        }

        mainPath = `/category/${route.slug || route.name}`;
        console.log('🚀 ~ renderPage ~ route.slugAliases:', route.slugAliases);
        route.slugAliases?.forEach((slugAliase) => aliasPaths.push(`/category/${slugAliase}`));
        break;
      case 'category':
        if (route?.subType === 'staticPage') {
          component = <StaticPageConstructor page={route.name} />;
        } else {
          sections = pageConfigs.filter((f: any) => f.type === route.type)[0]?.sections;
          component = (
            <CategoryPageConstructor
              type={route.type}
              categorySlug={route.slug}
              categoryName={route.name}
              settings={sections}
            />
          );
        }

        mainPath = `/category/${route.slug}`;
        route.slugAliases?.forEach((slugAliase) => aliasPaths.push(`/category/${slugAliase}`));
        break;
      case 'content':
        const contentPages = pageConfigs.filter((f: any) => f.type === route.type);
        sections = contentPages.find((c: any) => c.sections[route.name])?.sections;
        component = <ContentPageConstructor settings={sections} />;
        mainPath = `/content/${route.name}`;
        break;
      default:
        component = <></>;
        mainPath = '';
    }

    if (route.isDefault) {
      if (route.type === 'category' && selectedCategory === 'home') {
        _setSelectedCategory(route.name);
        _setSelectedTag(route.hasHomeTag ? 'home' : 'allGames');
        _setSelectedProvider(['all_providers']);
      }

      setDefaultPage(component);
    }

    const paths = [mainPath];

    if (childrenPaths.length) {
      childrenPaths.reduce((prev, childrenPath) => {
        const path = `${prev}/${childrenPath}`;
        paths.push(path);

        return path;
      }, mainPath);
    }

    if (aliasPaths.length) {
      aliasPaths.forEach((aliasPath) => {
        paths.push(`${aliasPath}`);
      });
    }

    return {
      element: component,
      paths,
    };
  };

  const routes: Route[] = useMemo(() => {
    const routesArr: Route[] = [];
    if (navigation && !!pageConfigs) {
      navigation?.forEach((route: NavigationItem) => {
        routesArr.push(renderPage(route));
      });
    }
    return routesArr;
  }, [navigation, pageConfigs, selectedCategory]);

  useEffect(() => {
    if (isTokenExpired()) {
      _logout();
    }
  }, []);

  return (
    <Suspense fallback={<div />}>
      <Routes>
        {process.env.REACT_APP_WEBSITENAME === 'PapiGames' && (
          <>
            {PAPIGAMES_AFFILATES.map((affilate) => (
              <Route
                path={`/${affilate.name}`}
                key={affilate.id}
                element={<MainLayout children={defaultPage ?? routes[0]} />}
              />
            ))}
          </>
        )}
        <Route path={'/'} element={<MainLayout children={defaultPage ?? routes[0]} />} />

        {routes && !!routes.length && (
          <Route
            path={'/*'}
            element={
              <Suspense fallback={null}>
                <NotFoundPage />
              </Suspense>
            }
          />
        )}

        {routes?.map((route) => {
          return route.paths.map((path) => (
            <Route key={path} path={path} element={<MainLayout children={route.element} />} />
          ));
        })}
      </Routes>
    </Suspense>
  );
};
export default Routing;
